import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bell, Lock } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const getVisitorId = () => {
  const KEY = 'vibesprint_visitor_id';
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = `vis_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
};

/**
 * PlaceholderCard — reusable "Coming soon / locked" cell for Settings pages.
 * Optional waitlistFeature wires a real backend opt-in (POST /api/waitlist).
 */
export const PlaceholderCard = ({
  icon: Icon = Lock,
  title,
  description,
  badge = 'Coming soon',
  primaryLabel,
  primaryTo,
  onPrimary,
  waitlistFeature,
  testIdBase = 'placeholder',
}) => {
  const storageKey = waitlistFeature
    ? `vibesprint_wl_${waitlistFeature}`
    : null;
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    if (storageKey && localStorage.getItem(storageKey)) setJoined(true);
  }, [storageKey]);

  const handleNotify = async () => {
    if (!waitlistFeature) return;
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        feature: waitlistFeature,
        visitor_id: getVisitorId(),
        source: 'settings_placeholder',
      });
      if (storageKey) localStorage.setItem(storageKey, 'true');
      setJoined(true);
      toast.success("You're on the list", {
        description: "We'll ping you the moment this ships.",
      });
    } catch (_e) {
      toast.error('Could not join the waitlist right now');
    }
  };

  return (
    <Card
      className="p-6 border-dashed border-primary/30 bg-gradient-to-br from-primary/[0.03] via-background to-background"
      data-testid={`${testIdBase}-placeholder-card`}
    >
      <div className="flex items-start gap-4">
        <div className="rounded-md bg-primary/10 p-2.5 flex-shrink-0">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-base font-semibold" data-testid={`${testIdBase}-placeholder-title`}>
              {title}
            </h3>
            <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
              {badge}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground max-w-xl">{description}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {primaryLabel && (
              primaryTo ? (
                <Button asChild className="btn-cta" data-testid={`${testIdBase}-primary`}>
                  <Link to={primaryTo}>{primaryLabel}</Link>
                </Button>
              ) : (
                <Button
                  onClick={onPrimary}
                  className="btn-cta"
                  data-testid={`${testIdBase}-primary`}
                >
                  {primaryLabel}
                </Button>
              )
            )}
            {waitlistFeature && (
              joined ? (
                <Badge variant="secondary" className="self-center" data-testid={`${testIdBase}-joined`}>
                  <Bell className="h-3 w-3 mr-1" />
                  You&apos;re on the list
                </Badge>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleNotify}
                  data-testid={`${testIdBase}-notify`}
                >
                  <Bell className="h-4 w-4 mr-1.5" />
                  Notify me
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PlaceholderCard;
